<template>
  <PVTabs
    v-bind="tabsProps"
    :data-testid="dataTestid"
    :value="model"
    class="d-flex flex-column w-100"
    @update:value="onValueUpdate"
  >
    <slot />
  </PVTabs>
</template>

<script lang="ts">
import type { TabsProps } from 'primevue/tabs';

export type StandardTabsProps = Pick<
  TabsProps,
  'lazy' | 'scrollable' | 'showNavigators' | 'tabindex' | 'selectOnFocus' | 'pt'
> & {
  dataTestid?: string;
};

export type StandardTabsBindProps = Pick<
  TabsProps,
  'lazy' | 'scrollable' | 'showNavigators' | 'tabindex' | 'selectOnFocus' | 'pt'
> &
  Record<string, unknown>;

/**
 * Until Product has official tab design updates, this is a general themed PrimeVue Tabs wrapper
 * (default slot: PVTabList / PVTab).
 *
 * Only current use case of it (as of writing this) is for SocGen's YouTrack platform,
 * where we replace the sidebar analysis steps with these tabs.
 */
export default {
  name: 'StandardTabs',
  compatConfig: { MODE: 3 },
  inheritAttrs: false,
};
</script>

<script setup lang="ts">
import { computed, useAttrs } from 'vue';

const model = defineModel<string | number>({ required: true });

const props = withDefaults(defineProps<StandardTabsProps>(), {
  lazy: false,
  scrollable: false,
  showNavigators: true,
  tabindex: 0,
  selectOnFocus: false,
});

const attrs = useAttrs();

const tabsProps = computed(
  (): StandardTabsBindProps => ({
    ...attrs,
    lazy: props.lazy,
    scrollable: props.scrollable,
    showNavigators: props.showNavigators,
    tabindex: props.tabindex,
    selectOnFocus: props.selectOnFocus,
    pt: props.pt,
  }),
);

const onValueUpdate = (value: string | number): void => {
  model.value = value;
};
</script>
